import {
  digitsOnly,
  formatCnpj,
  formatCpf,
  isValidCnpj,
  isValidCpf,
} from '@econdominio/core/documento';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input } from '../components/form';
import { Alert, Button, Card, EmptyState, Spinner } from '../components/ui';
import styles from './screens.module.css';

interface Pessoa {
  id: number;
  nome: string;
  /** Documento completo; a API só o devolve ao síndico (contábil recebe null). */
  cpfCnpj: string | null;
  /** Documento parcializado para exibição (RNF-L01). */
  cpfCnpjMascarado: string | null;
  email: string | null;
  telefone: string | null;
}

interface RespostaPessoa {
  pessoa?: Pessoa;
  error?: string;
}

type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; pessoas: Pessoa[] };

type Campo = 'nome' | 'cpfCnpj';

const MSG_NOME = 'Informe o nome.';
const MSG_DOCUMENTO = 'CPF ou CNPJ inválido. Confira os dígitos.';

/** Erros de validação da API que pertencem a um campo do formulário. */
const ERRO_DE_CAMPO: Record<string, { campo: Campo; mensagem: string }> = {
  invalid_nome: { campo: 'nome', mensagem: MSG_NOME },
  invalid_cpf_cnpj: { campo: 'cpfCnpj', mensagem: MSG_DOCUMENTO },
};

/** Demais erros da API (código → mensagem), mostrados acima da lista. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  not_found: 'Pessoa não encontrada. A lista foi atualizada.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível salvar. Tente novamente.';

/** Documento vazio ou com dígitos verificadores válidos (CPF 11 / CNPJ 14). */
function documentoValido(texto: string): boolean {
  const d = digitsOnly(texto);
  return d === '' || (d.length === 11 && isValidCpf(d)) || (d.length === 14 && isValidCnpj(d));
}

/** Formata o documento guardado (só dígitos) para o campo de edição. */
function formatarDocumento(doc: string | null): string {
  if (!doc) return '';
  return doc.length === 14 ? formatCnpj(doc) : formatCpf(doc);
}

/**
 * Pessoas do condomínio (RF-A03). `editavel` só para o síndico de condomínio ativo:
 * POST/PATCH são `requireRole('sindico')`, o contábil é somente leitura e o tenant
 * suspenso também (#127). Vínculos com unidades ficam na tela de Vínculos.
 */
export function Pessoas({ editavel }: { editavel: boolean }) {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  // Só o id: a pessoa em edição vem sempre da lista atual.
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [aviso, setAviso] = useState<{ kind: 'success' | 'danger'; texto: string } | null>(null);

  const carregar = useCallback(async () => {
    const dados = await getJson<{ pessoas: Pessoa[] }>('/api/pessoas');
    setCarga(dados ? { estado: 'ok', pessoas: dados.pessoas } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Erro da API fora dos campos; pessoa inexistente fecha a edição e recarrega. */
  async function tratarFalha(codigo?: string) {
    setAviso({ kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO });
    if (codigo !== 'not_found') return;
    setEditandoId(null);
    await carregar();
  }

  async function aoSalvar(nome: string) {
    setAviso({ kind: 'success', texto: `Pessoa ${nome} salva.` });
    setEditandoId(null);
    await carregar();
  }

  if (carga.estado === 'carregando') return <Spinner />;
  if (carga.estado === 'erro') {
    return <Alert kind="danger">Não foi possível carregar as pessoas.</Alert>;
  }

  const { pessoas } = carga;
  const emEdicao = pessoas.find((p) => p.id === editandoId) ?? null;
  return (
    <div className={styles.stack}>
      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      {editavel ? (
        <PessoaForm
          key={emEdicao?.id ?? 'nova'}
          pessoa={emEdicao}
          onSalva={aoSalvar}
          onCancelar={() => setEditandoId(null)}
          onErroGeral={tratarFalha}
        />
      ) : null}

      {pessoas.length === 0 ? (
        <EmptyState>Nenhuma pessoa cadastrada.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF/CNPJ</th>
                <th>E-mail</th>
                <th>Telefone</th>
                {editavel ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {pessoas.map((p) => (
                <tr key={p.id}>
                  <td>{p.nome}</td>
                  <td>{p.cpfCnpjMascarado ?? '—'}</td>
                  <td>{p.email ?? '—'}</td>
                  <td>{p.telefone ?? '—'}</td>
                  {editavel ? (
                    <td>
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={`Editar pessoa ${p.nome}`}
                        onClick={() => {
                          setAviso(null);
                          setEditandoId(p.id);
                        }}
                      >
                        Editar
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Formulário de criação (`pessoa` nula) ou edição; erros de validação vão para o campo. */
function PessoaForm({
  pessoa,
  onSalva,
  onCancelar,
  onErroGeral,
}: {
  pessoa: Pessoa | null;
  onSalva: (nome: string) => Promise<void>;
  onCancelar: () => void;
  /** Erro da API fora dos campos (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  const [nome, setNome] = useState(pessoa?.nome ?? '');
  const [cpfCnpj, setCpfCnpj] = useState(formatarDocumento(pessoa?.cpfCnpj ?? null));
  const [email, setEmail] = useState(pessoa?.email ?? '');
  const [telefone, setTelefone] = useState(pessoa?.telefone ?? '');
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    const locais: Partial<Record<Campo, string>> = {};
    if (!nome.trim()) locais.nome = MSG_NOME;
    if (!documentoValido(cpfCnpj)) locais.cpfCnpj = MSG_DOCUMENTO;
    setErros(locais);
    if (locais.nome || locais.cpfCnpj) return;

    setEnviando(true);
    const corpo = { nome: nome.trim(), cpfCnpj, email, telefone };
    const res = pessoa
      ? await mutateJson<RespostaPessoa>(
          `/api/pessoas/${encodeURIComponent(pessoa.id)}`,
          corpo,
          'PATCH',
        )
      : await mutateJson<RespostaPessoa>('/api/pessoas', corpo);
    setEnviando(false);

    if (res.ok) {
      if (!pessoa) {
        setNome('');
        setCpfCnpj('');
        setEmail('');
        setTelefone('');
      }
      await onSalva(res.data?.pessoa?.nome ?? corpo.nome);
      return;
    }
    const codigo = res.data?.error;
    const deCampo = codigo ? ERRO_DE_CAMPO[codigo] : undefined;
    if (deCampo) setErros({ [deCampo.campo]: deCampo.mensagem });
    else await onErroGeral(codigo);
  }

  return (
    <Card title={pessoa ? `Editar pessoa ${pessoa.nome}` : 'Nova pessoa'}>
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Nome" error={erros.nome}>
            {(id) => (
              <Input
                id={id}
                value={nome}
                maxLength={120}
                onChange={(e) => setNome(e.target.value)}
              />
            )}
          </Field>
          <Field label="CPF/CNPJ" error={erros.cpfCnpj}>
            {(id) => (
              <Input
                id={id}
                value={cpfCnpj}
                inputMode="numeric"
                placeholder="000.000.000-00"
                onChange={(e) => setCpfCnpj(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Field label="E-mail">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Field label="Telefone">
            {(id) => (
              <Input
                id={id}
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Button type="submit" disabled={enviando}>
            {pessoa ? 'Salvar' : 'Adicionar'}
          </Button>
          {pessoa ? (
            <Button variant="ghost" onClick={onCancelar}>
              Cancelar
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
